import React from "react";
//#region types
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { CustomerFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { EventConstants, IconDefinitionConstants, ReportConstants } from "utils/constants";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import {
  ChartContextProvider,
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  ReportSearchInput,
  SearchContextProvider,
  StyledButton,
  ViewPanel,
  WidgetArea,
} from "components/common";
import { Grid } from "@mui/material";
import CustomerList from "./customerList";
import * as CustomerWidgets from "./widgets";
import useCustomerReportFilterDefinition from "./forms/useReportFilterDefinition";
import { useNavigate, useLocation } from "react-router-dom";
import { useCustomerService } from "serviceHooks";
import useImportCustomerDialog from "./import/useImportCustomer";
import { useInvoicePermissions } from "components/invoice";
//#endregion
import { EventHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const CustomerReport: React.FC = () => {
  const customerService = useCustomerService();
  const location = useLocation();
  const navigate = useNavigate();

  const { hasChartsPermissions, canUseInvoices, canCreateInvoices, getIsInvoiceAdmin } = useInvoicePermissions();

  const reportKey = ReportConstants.ReportKeys.Customers;

  const { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter } = useCustomerReportFilterDefinition();
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.Customers);

  const refreshReport = () => {
    EventHelper.publishEvent(EventHelper.getDynamicEventName(EventConstants.RefreshReport, reportKey));
  };
  const { showImportCustomerDialog } = useImportCustomerDialog();

  const onCustomerCreateClick = () => {
    navigate(UrlHelper.formatPath(`/Customer/Create`), {
      state: { background: location, preserveSearch: true },
    });
  };

  const tableRef = React.useRef<TableHandles>(null);

  const exportReportExcel = async () => {
    tableRef.current?.exportRawDataToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportRawDataToCSV();
  };

  const onImportCustomerClick = async () => {
    showImportCustomerDialog(refreshReport);
  };

  const getTopButtons = () => {
    let buttons: (React.ReactElement | typeof ACTION_SEPARATOR)[] = [
      <ReportSearchInput
        placeholder={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SearchCustomer)}
      />,
    ];

    if (canCreateInvoices) {
      buttons = buttons.concat([
        ACTION_SEPARATOR,
        <StyledButton size="sm" onClick={onCustomerCreateClick}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreateClient)}
        </StyledButton>,
      ]);
    }

    buttons = buttons.concat([ACTION_SEPARATOR, <FilterButton />]);

    return buttons;
  };

  const topRightButtons = (
    <DashboardButtonGroup
      buttons={getTopButtons()}
      moreActions={
        getIsInvoiceAdmin()
          ? [
              {
                description: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Customer.ExportCustomerRegisterToEXCEL
                ),
                icon: IconDefinitionConstants.WSave,
                onClickHandler: exportReportExcel,
              },
              {
                description: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Customer.ExportCustomerRegisterToCSV
                ),
                icon: IconDefinitionConstants.WSave,
                onClickHandler: exportReportCSV,
              },
              {
                description: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Customer.ImportCustomerRegister
                ),
                icon: IconDefinitionConstants.WCloudUpload,
                onClickHandler: onImportCustomerClick,
              },
            ]
          : []
      }
    />
  );

  return (
    <ReloadEvent
      uniqueKey={reportKey}
      render={(reloadKey, reload) => (
        <SearchContextProvider>
          <PageLayouts.DashboardLayout<CustomerFormTypes.Filter>
            entityKey={0}
            hasPermissions={canUseInvoices}
            title={title}
            topRightButtons={topRightButtons}
            filterFields={filterFormFields}
            filterQueryStateHandler={filterQueryStateHandler}
            filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.Customer}
            initialFilter={{ active: true }}
            reportToAPIFilterFormatter={customerService.reportToAPIFilter}
            apiToReportFilterFormatter={apiToReportFilterFormatter}
          >
            {hasChartsPermissions() ? (
              <WidgetArea>
                <ChartContextProvider>
                  <ViewPanel
                    title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Rating)}
                    appearance="sm"
                    gridSize="md"
                    isCanvasContent
                  >
                    <CustomerWidgets.CustomerRatingChart />
                  </ViewPanel>
                  <ViewPanel
                    title={LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Customer.HighestInvoicedAmount
                    )}
                    appearance="sm"
                    gridSize="md"
                    isCanvasContent
                  >
                    <CustomerWidgets.CustomerHighestInvoicesChart />
                  </ViewPanel>
                </ChartContextProvider>
              </WidgetArea>
            ) : (
              <></>
            )}
            <Grid container item>
              <CustomerList ref={tableRef} exportFileName={title} reloadKey={reloadKey} />
            </Grid>
          </PageLayouts.DashboardLayout>
        </SearchContextProvider>
      )}
    />
  );
};

export default CustomerReport;
